// Página "Todas las plantillas" — destino del CTA "Ver todas las
// plantillas" que aparece en la home de Interfaz V2 en desktop.
//
// Layout:
//   • Arriba: row horizontal de chips (una por categoría / colección),
//     scrolleable y multi-select. Si no hay ninguno activo, se muestran
//     todas las plantillas.
//   • Abajo: grilla tipo masonry (CSS columns) con tarjetas de alto
//     variable según el aspect natural de la imagen del preset. Cada
//     card tiene la imagen + overlay con título abajo a la izquierda.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Play } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useSidebar } from '../../context/SidebarContext';
import type { CollectionV2, PresetV2 } from './mockData';
import { fetchTemplatesV2 } from './templatesApi';
import { fetchAllPoses } from './posesApi';

// Rubros que ven la categoría virtual "E-commerce" (poses como cards).
// Mismo set que `DESIGN_RUBROS` en InterfazV2 — duplicado para no crear
// una dependencia circular del archivo de la home.
const DESIGN_RUBROS = new Set([
  'Moda e Indumentaria',
  'Belleza y Cuidado Personal',
]);

// Orden custom del row de E-commerce: cuerpo_completo + plano_medio
// primero, detalle_close-up al final, resto en el medio.
const ecommerceCategoryRank = (cat: string | null): number => {
  if (cat === 'cuerpo_completo') return 0;
  if (cat === 'plano_medio') return 1;
  if (cat === 'detalle_close-up') return 3;
  return 2;
};

// State persistente entre mount/unmount — al volver desde un preset
// queremos ver la grilla al toque (sin skeleton) y restaurar el scroll
// + la categoría activa para que se sienta como una pantalla
// "siempre montada".
const PLANTILLAS_CACHE_KEY = 'atlas-v2-plantillas-cache';
const PLANTILLAS_SCROLL_KEY = 'atlas-v2-plantillas-scroll';
const PLANTILLAS_CATEGORY_KEY = 'atlas-v2-plantillas-category';

interface FlatPreset extends PresetV2 {
  /** id de la categoría de origen — sirve para pasar `collectionId` al
   *  abrir el sheet en /interfaz-v2 (mismo patrón que ColeccionDetalle). */
  collectionId: string;
  collectionTitle: string;
}

const InterfazV2Plantillas: React.FC = () => {
  const navigate = useNavigate();
  const { sidebarWidth } = useSidebar();

  // Initializer lee el cache: si el user ya visitó esta página antes
  // en la sesión, render instantáneo sin skeleton.
  const [collections, setCollections] = useState<CollectionV2[] | null>(() => {
    try {
      const raw = sessionStorage.getItem(PLANTILLAS_CACHE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed as CollectionV2[];
      }
    } catch {
      /* noop */
    }
    return null;
  });
  // Single-select: 'all' = mostrar todas las plantillas (default).
  // Persistimos en sessionStorage así, al volver desde un preset, el
  // user queda en la categoría que tenía activa.
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>(() => {
    try {
      return sessionStorage.getItem(PLANTILLAS_CATEGORY_KEY) || 'all';
    } catch {
      return 'all';
    }
  });
  useEffect(() => {
    try {
      sessionStorage.setItem(PLANTILLAS_CATEGORY_KEY, selectedCategoryId);
    } catch {
      /* noop */
    }
  }, [selectedCategoryId]);

  // Sticky fake-out: `position: sticky` no funciona acá porque el
  // `<main>` de `DashboardLayout` tiene `overflow-x-hidden` (CSS lo
  // computa a `overflow-y: auto`) y eso lo vuelve el "scroll container"
  // ancestral del sticky — pero MAIN no scrollea de verdad (es body
  // el que scrollea), así que el sticky queda congelado. Workaround:
  // observamos un sentinel arriba del row de chips; cuando sale por
  // arriba del viewport, swapeamos el chip-bar a `position: fixed`.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const chipsBarRef = useRef<HTMLDivElement | null>(null);
  const [isStuck, setIsStuck] = useState(false);
  const [chipsHeight, setChipsHeight] = useState(0);
  const [isMobileView, setIsMobileView] = useState(false);
  useEffect(() => {
    const check = () => setIsMobileView(window.innerWidth < 640);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Stuck cuando el sentinel salió por arriba del viewport
        // (no intersecta y su top quedó por encima del viewport).
        setIsStuck(
          !entry.isIntersecting && entry.boundingClientRect.top < 0,
        );
      },
      { threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [collections]);
  // Medimos la altura del chip-bar para meter un spacer del mismo
  // tamaño en el flow cuando se fija — evita que el grid salte.
  useEffect(() => {
    if (!chipsBarRef.current) return;
    setChipsHeight(chipsBarRef.current.offsetHeight);
  }, [collections, isStuck]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        let rubro: string | null = null;
        let companyId: string | null = null;
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('id_company')
            .eq('id', user.id)
            .single();
          const idCompany = (profile as any)?.id_company as string | undefined;
          if (idCompany) {
            companyId = idCompany;
            const { data: company } = await supabase
              .from('company_profiles')
              .select('rubro')
              .eq('id_company', idCompany)
              .single();
            rubro = (company as any)?.rubro || null;
          }
        }
        const list = await fetchTemplatesV2({ rubro, companyId });
        // Si el rubro califica, también fetcheamos las poses y armamos
        // la colección virtual "E-commerce" para mostrarla acá (sino
        // queda solo accesible desde el mobile home).
        const wantsEcommerce = rubro ? DESIGN_RUBROS.has(rubro) : false;
        let withEcommerce = list;
        if (wantsEcommerce) {
          try {
            const poses = await fetchAllPoses();
            if (poses.length > 0) {
              const ecommerce: CollectionV2 = {
                id: 'e-commerce',
                title: 'Fondo blanco',
                subtitle: undefined,
                presets: [...poses]
                  .sort(
                    (a, b) =>
                      ecommerceCategoryRank(a.categoria) -
                      ecommerceCategoryRank(b.categoria),
                  )
                  .map(
                    (p): PresetV2 => ({
                      id: `pose-${p.id}`,
                      title: p.nombre,
                      imageUrl: p.imageUrl,
                      imageUrlFull: p.imageUrl,
                      kind: 'design' as const,
                    }),
                  ),
              };
              withEcommerce = [ecommerce, ...list];
            }
          } catch {
            // silent — si las poses fallan seguimos con la lista normal
          }
        }
        if (alive) {
          setCollections(withEcommerce);
          try {
            sessionStorage.setItem(
              PLANTILLAS_CACHE_KEY,
              JSON.stringify(withEcommerce),
            );
          } catch {
            /* noop */
          }
        }
      } catch {
        // Si falla el fetch pero tenemos cache previo, conservalo.
        if (alive) setCollections((prev) => prev ?? []);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Restore scroll al volver desde un preset. Si no hay scroll guardado,
  // no hacemos nada — el browser preservará su propio scroll en
  // first-mount fresh, o caerá a top si es navegación normal.
  useEffect(() => {
    try {
      const sy = sessionStorage.getItem(PLANTILLAS_SCROLL_KEY);
      if (!sy) return;
      const y = Number(sy);
      sessionStorage.removeItem(PLANTILLAS_SCROLL_KEY);
      // Doble rAF: el primero da chance al cache de la grilla a
      // renderizarse; el segundo asegura que el layout ya midió.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo({ top: y });
          document.documentElement.scrollTo({ top: y });
        });
      });
    } catch {
      /* noop */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Categorías reales (excluimos la "design-row" virtual si vino — esta
  // página solo muestra plantillas tradicionales, no los modos).
  const categories = useMemo(() => {
    const real = (collections || []).filter((c) => c.id !== 'design-row');
    // Pedido del jefe: "Alfombra roja" va al FINAL de las pills (no le
    // convence). La movemos al fondo manteniendo el orden del resto.
    const isAlfombraRoja = (title?: string) =>
      (title || '').trim().toLowerCase() === 'alfombra roja';
    return [
      ...real.filter((c) => !isAlfombraRoja(c.title)),
      ...real.filter((c) => isAlfombraRoja(c.title)),
    ];
  }, [collections]);

  // Plantilla flat: cada preset con su collectionId/Title de origen. Si
  // un preset apareciera en varias categorías la dedupeamos por preset.id
  // dándole la primera categoría que lo contiene.
  const allPresets = useMemo<FlatPreset[]>(() => {
    const seen = new Set<string>();
    const out: FlatPreset[] = [];
    for (const c of categories) {
      for (const p of c.presets) {
        if (seen.has(p.id)) continue;
        seen.add(p.id);
        out.push({ ...p, collectionId: c.id, collectionTitle: c.title });
      }
    }
    return out;
  }, [categories]);

  const filteredPresets = useMemo(() => {
    if (selectedCategoryId === 'all') return allPresets;
    return allPresets.filter((p) => p.collectionId === selectedCategoryId);
  }, [allPresets, selectedCategoryId]);

  const openPreset = (p: FlatPreset) => {
    // Guardamos scroll para restaurar al volver — sin esto la página
    // se monta de nuevo al hacer back y arranca scrolleada hasta arriba.
    try {
      sessionStorage.setItem(PLANTILLAS_SCROLL_KEY, String(window.scrollY));
    } catch {
      /* noop */
    }
    // Buscamos la collection real para pasar sus presets como
    // previewList — así el sheet puede swipear entre todos.
    const sourceCollection = (collections ?? []).find(
      (c) => c.id === p.collectionId,
    );
    navigate(`/interfaz-v2?preset=${p.id}`, {
      state: {
        presetId: p.id,
        collectionId: p.collectionId,
        // Data sincrónica para que InterfazV2 abra el sheet en el
        // primer paint sin un flash blanco mientras carga el fetch.
        presetData: p,
        collectionPresets: sourceCollection?.presets ?? [p],
      },
    });
  };

  // Al cambiar de categoría volvemos suavemente al tope del scroll —
  // así el user ve el inicio de la grilla filtrada sin tener que
  // scrollear hacia arriba a mano. Probamos `window.scrollTo` y
  // `documentElement.scrollTo` porque, dependiendo de cómo el browser
  // resuelva el scroll context de DashboardLayout, uno u otro es el
  // que efectivamente scrollea.
  const selectCategory = (id: string) => {
    if (id === selectedCategoryId) return;
    setSelectedCategoryId(id);
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      window.scrollTo(0, 0);
    }
  };

  const MobileOverlay = (
    <div
      className="fixed inset-0 bg-white pointer-events-none sm:hidden"
      aria-hidden="true"
    />
  );

  if (collections === null) {
    return (
      <>
        {MobileOverlay}
        <div className="relative min-h-[100dvh] bg-surface-mobile sm:bg-surface-content" />
      </>
    );
  }

  return (
    <>
      {MobileOverlay}
      <div className="relative min-h-[100dvh] bg-surface-mobile sm:bg-surface-content">
        <div className="max-w-content mx-auto px-5 sm:px-8 pt-4 sm:pt-8 pb-24">
          {/* Header con back + título */}
          <header className="mb-5 sm:mb-6">
            <button
              type="button"
              onClick={() => navigate('/interfaz-v2')}
              className="hidden sm:inline-flex items-center gap-1 text-brand-primary text-[14px] mb-4 hover:opacity-75 transition-opacity"
            >
              <ChevronLeft className="w-4 h-4" />
              Volver
            </button>
            <h1 className="text-[24px] sm:text-[32px] font-semibold text-brand-black/90 leading-tight">
              Plantillas
            </h1>
          </header>

          {/* Row horizontal de chips (categorías). Single-select: el
              primer chip "Todas las plantillas" muestra todo. Sólo un
              chip queda activo a la vez. Para sticky usamos un
              `IntersectionObserver` + position swap (ver useEffect
              arriba): cuando el sentinel sale del viewport por arriba,
              el bar pasa a `position: fixed top: 0` y mostramos un
              spacer del mismo alto para que el grid no salte. */}
          {categories.length > 0 && (
            <>
              <div ref={sentinelRef} aria-hidden="true" />
              <div
                ref={chipsBarRef}
                className={
                  // Estado natural: fondo sólido con borde inferior y
                  // negative margins para sangrar borde a borde.
                  // Estado fixed: fondo en degradé top→bottom (sólido
                  // arriba, transparente abajo) — sin borde duro y sin
                  // negative margins (porque pisarían el sidebar). El
                  // stop al ~70% deja la transición "repentina" como
                  // pidió el user en vez de un fade suave.
                  isStuck
                    ? 'fixed top-0 right-0 z-30 pb-3 sm:pb-4'
                    : 'bg-surface-mobile sm:bg-surface-content border-b border-black/[0.06] -mx-3 sm:-mx-5 relative mb-3 sm:mb-4'
                }
                style={
                  isStuck
                    ? {
                        left: isMobileView ? 0 : sidebarWidth,
                        background: isMobileView
                          ? 'linear-gradient(to bottom, #FFFFFF 0%, #FFFFFF 70%, rgba(255,255,255,0) 100%)'
                          : 'linear-gradient(to bottom, #FBFBFD 0%, #FBFBFD 70%, rgba(251,251,253,0) 100%)',
                      }
                    : undefined
                }
              >
                <div
                  className={`flex gap-2 overflow-x-auto scrollbar-hide py-3 sm:py-4 ${
                    isStuck
                      ? 'max-w-content mx-auto px-5 sm:px-8'
                      : 'px-3 sm:px-5'
                  }`}
                  style={{
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    WebkitOverflowScrolling: 'touch',
                  }}
                >
                  {[{ id: 'all', title: 'Todas las plantillas' }, ...categories].map((c, i) => {
                    const active = selectedCategoryId === c.id;
                    const isFirstCategory = i === 1;
                    return (
                      <React.Fragment key={c.id}>
                        {isFirstCategory && (
                          <span
                            aria-hidden="true"
                            className="shrink-0 self-center w-px h-5 bg-black/10 mx-1"
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => selectCategory(c.id)}
                          className={`shrink-0 px-4 py-2 rounded-full text-[14px] font-medium transition-colors border ${
                            active
                              ? 'border-brand-primary text-brand-primary bg-brand-primary/[0.04]'
                              : 'border-brand-black/80 text-brand-black/90 bg-white hover:bg-brand-black/[0.03]'
                          }`}
                        >
                          {c.title}
                        </button>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
              {/* Spacer cuando está fijo — mantiene el flow del grid
                  para que no salte hacia arriba al desconectarse el
                  bar del documento. */}
              {isStuck && (
                <div
                  aria-hidden="true"
                  style={{ height: chipsHeight, marginBottom: 12 }}
                />
              )}
            </>
          )}

          {/* Grilla masonry — CSS columns. Cada card se renderiza con su
              aspect natural; los `break-inside-avoid` evitan que el
              navegador corte una card entre columnas. */}
          {filteredPresets.length === 0 ? (
            <div className="text-[14px] text-[#3C3C43]/60">
              {categories.length === 0
                ? 'Todavía no hay plantillas disponibles para tu rubro.'
                : 'No hay plantillas para los filtros seleccionados.'}
            </div>
          ) : (
            <div
              // `key` ligado a la categoría → React remonta el div
              // cuando cambia el filtro, lo que dispara la animación
              // `category-swap` (fade + slide horizontal sutil desde
              // la derecha) en cada swap.
              key={selectedCategoryId}
              className="columns-2 sm:columns-3 lg:columns-4 gap-3 sm:gap-4 animate-category-swap"
            >
              {filteredPresets.map((p, i) => (
                <MasonryCard
                  key={p.id}
                  preset={p}
                  // Patrón cíclico de aspect ratios para que aún cuando
                  // todas las imágenes originales sean 3:4, la grilla
                  // se vea variada estilo galería. Los cards se croppean
                  // con `object-cover`, así no se distorsiona la imagen.
                  aspectClass={ASPECT_CYCLE[i % ASPECT_CYCLE.length]}
                  onClick={() => openPreset(p)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// Patrón cíclico de aspect ratios para el masonry. Mezcla verticales
// (3/4, 2/3) con cuadrados (1/1) y semi-verticales (4/5, 5/6) para
// generar una galería con alturas variadas aun cuando todas las
// imágenes fuente sean 3:4. El patrón es primo con 4 (columnas
// desktop) y 2 (columnas mobile) para que no se forme un grid
// repetitivo por columna.
const ASPECT_CYCLE = [
  'aspect-[3/4]',
  'aspect-[4/5]',
  'aspect-[1/1]',
  'aspect-[2/3]',
  'aspect-[3/4]',
  'aspect-[5/6]',
  'aspect-[4/5]',
] as const;

interface MasonryCardProps {
  preset: FlatPreset;
  aspectClass: string;
  onClick: () => void;
}

const MasonryCard: React.FC<MasonryCardProps> = ({ preset, aspectClass, onClick }) => {
  const isVideo = preset.outputType === 'video';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full mb-3 sm:mb-4 break-inside-avoid rounded-xl overflow-hidden bg-neutral-100 relative group active:scale-[0.99] transition-transform block ${aspectClass}`}
    >
      {isVideo ? (
        <video
          src={preset.imageUrlFull || preset.imageUrl}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          poster={preset.imageUrl}
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500 ease-out"
        />
      ) : (
        <img
          src={preset.imageUrl}
          alt={preset.title}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500 ease-out"
        />
      )}
      {isVideo && (
        <span className="absolute top-2 right-2 w-7 h-7 inline-flex items-center justify-center rounded-full bg-black/40 backdrop-blur-sm text-white shadow">
          <Play className="w-3.5 h-3.5 fill-white" strokeWidth={0} />
        </span>
      )}
      {/* Overlay con título abajo a la izquierda — mismo gradient que las
          cards de los rows en la home. */}
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/55 via-black/10 to-transparent pointer-events-none" />
      <p className="absolute bottom-2.5 left-3 right-3 text-left text-white text-[13px] font-medium leading-tight drop-shadow">
        {preset.title}
      </p>
    </button>
  );
};

export default InterfazV2Plantillas;
